import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from './components/nuxt-error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_axios_6d2a0690 from 'nuxt_plugin_axios_6d2a0690' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_pluginutils_7d3859ab from 'nuxt_plugin_pluginutils_7d3859ab' // Source: ./nuxt-i18n/plugin.utils.js (mode: 'all')
import nuxt_plugin_pluginrouting_72edaf94 from 'nuxt_plugin_pluginrouting_72edaf94' // Source: ./nuxt-i18n/plugin.routing.js (mode: 'all')
import nuxt_plugin_pluginmain_029d9ef9 from 'nuxt_plugin_pluginmain_029d9ef9' // Source: ./nuxt-i18n/plugin.main.js (mode: 'all')
import nuxt_plugin_workbox_4c9317ac from 'nuxt_plugin_workbox_4c9317ac' // Source: ./workbox.js (mode: 'client')
import nuxt_plugin_metaplugin_e2401a82 from 'nuxt_plugin_metaplugin_e2401a82' // Source: ./pwa/meta.plugin.js (mode: 'all')
import nuxt_plugin_router_273951c2 from 'nuxt_plugin_router_273951c2' // Source: ./router.js (mode: 'all')
import nuxt_plugin_universal_82a2a37c from 'nuxt_plugin_universal_82a2a37c' // Source: ../src/plugins/universal.js (mode: 'all')
import nuxt_plugin_client_035ad0d4 from 'nuxt_plugin_client_035ad0d4' // Source: ../src/plugins/client.js (mode: 'client')
import nuxt_plugin_globalVar_3f2a5169 from 'nuxt_plugin_globalVar_3f2a5169' // Source: ../src/plugins/globalVar.js (mode: 'client')
import nuxt_plugin_firebasecompat_7c8a60e5 from 'nuxt_plugin_firebasecompat_7c8a60e5' // Source: ../src/plugins/firebase-compat.js (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root.$options.$nuxt
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"TopLupe","htmlAttrs":{"lang":"en"},"meta":[{"charset":"utf-8"},{"name":"viewport","id":"viewportMeta","content":"width=device-width,initial-scale=1.0,maximum-scale=1.0,user-scalable=no,viewport-fit=cover"},{"name":"apple-mobile-web-app-capable","content":"yes"},{"name":"apple-mobile-web-app-status-bar-style","content":"black"},{"name":"format-detection","content":"telephone=no"},{"name":"format-detection","content":"email=no"},{"httpEquiv":"cache-control","content":"no-cache"},{"name":"screen-orientation","content":"portrait"},{"name":"full-screen","content":"yes"},{"name":"x5-fullscreen","content":"true"},{"hid":"description","name":"description","content":""},{"name":"yandex-verification","content":"2572aefe1bf4ce26"}],"link":[{"rel":"icon","type":"image\u002Fx-icon","href":"\u002Ffavicon.ico"}],"style":[],"script":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (typeof nuxt_plugin_axios_6d2a0690 === 'function') {
    await nuxt_plugin_axios_6d2a0690(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginutils_7d3859ab === 'function') {
    await nuxt_plugin_pluginutils_7d3859ab(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginrouting_72edaf94 === 'function') {
    await nuxt_plugin_pluginrouting_72edaf94(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginmain_029d9ef9 === 'function') {
    await nuxt_plugin_pluginmain_029d9ef9(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_workbox_4c9317ac === 'function') {
    await nuxt_plugin_workbox_4c9317ac(app.context, inject)
  }

  if (typeof nuxt_plugin_metaplugin_e2401a82 === 'function') {
    await nuxt_plugin_metaplugin_e2401a82(app.context, inject)
  }

  if (typeof nuxt_plugin_router_273951c2 === 'function') {
    await nuxt_plugin_router_273951c2(app.context, inject)
  }

  if (typeof nuxt_plugin_universal_82a2a37c === 'function') {
    await nuxt_plugin_universal_82a2a37c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_client_035ad0d4 === 'function') {
    await nuxt_plugin_client_035ad0d4(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_globalVar_3f2a5169 === 'function') {
    await nuxt_plugin_globalVar_3f2a5169(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_firebasecompat_7c8a60e5 === 'function') {
    await nuxt_plugin_firebasecompat_7c8a60e5(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
